import React, { useCallback, useEffect, useState } from "react";
import "./App.css";
import { Grid } from "./components/Grid";
import { shuffleArray } from "./utils/shuffle";
import words from "./utils/data/data.json";
import { checkGuess } from "./utils/checkGuess";
import { Header } from "./components/Header";
import { updateStats } from "./utils/stats";
import { Stats } from "./components/Stats";
import { uniquePermutationsForMaskedWord } from "./utils/permutations";

const App = () => {
  const wordLength = 9;
  const numRows = 5;

  const emptyGrid = Array.from({ length: numRows }, () =>
    Array(wordLength).fill("")
  );

  const emptyColors = Array.from(
    { length: numRows },
    () => Array(wordLength).fill("empty") as ("empty" | "G" | "R")[]
  );

  const [guesses, setGuesses] = useState<string[][]>(emptyGrid);
  const [targetWord, setTargetWord] = useState<string>("");
  const [resultColors, setResultColors] =
    useState<("empty" | "G" | "R")[][]>(emptyColors);
  const [shuffledWord, setShuffledWord] = useState<string>("");
  const [activeRow, setActiveRow] = useState<number>(0);
  const [gameOver, setGameOver] = useState(false);
  const [isWin, setIsWin] = useState(false);
  const [usedLetters, setUsedLetters] = useState<string[]>([]);
  const [usedLetterIndices, setUsedLetterIndices] = useState<number[]>([]);
  const [flippingTiles, setFlippingTiles] = useState<
    { row: number; tile: number }[]
  >([]);
  const [showStats, setShowStats] = useState(false);
  const [showHowToPlay, setShowHowToPlay] = useState(false);
  const [gameResult, setGameResult] = useState<{
    isWin: boolean;
    correctWord: string;
  } | null>(null);
  const [letterPositions, setLetterPositions] = useState<number[]>([]);
  const [remainingPermutations, setRemainingPermutations] = useState<number>(0);

  const flipRow = useCallback(async (row: number) => {
    for (let i = 0; i < wordLength; i++) {
      setFlippingTiles((prev) => {
        const alreadyExists = prev.some(
          (tile) => tile.row === row && tile.tile === i
        );
        if (alreadyExists) return prev;
        return [...prev, { row, tile: i }];
      });
      await new Promise((res) => setTimeout(res, 200));
    }
  }, []);

  const generateWord = () => {
    const randomWord = words[Math.floor(Math.random() * words.length)];
    setTargetWord(randomWord);

    const shuffled = shuffleArray(randomWord.split("")).join("");
    setShuffledWord(shuffled);
    setLetterPositions([0, 1, 2, 3, 4, 5, 6, 7, 8]); // Add this line

    setGuesses(emptyGrid);
    setResultColors(emptyColors);
    setActiveRow(0);
    setUsedLetters([]);
    setUsedLetterIndices([]);
    setGameOver(false);
    setIsWin(false);
    setFlippingTiles([]);
    setGameResult(null);
    
    setTimeout(() => {
      const mask = Array(9).fill("empty");
      const count = uniquePermutationsForMaskedWord(randomWord, mask);
      setRemainingPermutations(count);
    }, 0);
  };

  useEffect(() => {
    generateWord();
  }, []);

  const updatePermutationsCount = useCallback(() => {
  // Create a mask from your resultColors for the current active row
  const mask = resultColors[activeRow].map(color => 
    color === "G" ? "G" : color === "R" ? "R" : "empty"
  );
  
  const count = uniquePermutationsForMaskedWord(targetWord, mask);
  setRemainingPermutations(count);
}, [targetWord, resultColors, activeRow]);

  const isValidRow = useCallback(() => {
    const row = guesses[activeRow];
    const targetLetters = targetWord.split("").sort();
    const rowLetters = row.slice().sort();
    return rowLetters.join("") === targetLetters.join("");
  }, [guesses, activeRow, targetWord]);

  const handleShuffle = () => {
    if (gameOver) return;

    const newOrder = shuffleArray([...letterPositions]);
    setLetterPositions(newOrder);
  };

  const handleSubmit = useCallback(async () => {
    if (!isValidRow() || gameOver) return;

    const currentGuess = guesses[activeRow];
    const colors: ("empty" | "G" | "R")[] = checkGuess(
      targetWord.split(""),
      currentGuess
    );

    const mask = colors.map(color => color);
    const count = uniquePermutationsForMaskedWord(targetWord, mask);
    setRemainingPermutations(count);

    setResultColors((prevColors) => {
      const newColors = prevColors.map((rowColors, index) => {
        if (index === activeRow) {
          return [...colors];
        } else {
          const newRow = [...rowColors];
          return newRow;
        }
      });
      return newColors;
    });

    await flipRow(activeRow);

    const rowWin = colors.every((c) => c === "G");

    if (rowWin) {
      const attempts = activeRow + 1;
      updateStats(true, attempts); // Only update stats on win
      setGameOver(true);
      setIsWin(true);
      setGameResult({ isWin: true, correctWord: targetWord });
      setShowStats(true);
      return;
    }

    if (activeRow >= numRows - 1) {
      updateStats(false); // Only update stats on loss (no attempts parameter)
      setGameOver(true);
      setIsWin(false);
      setGameResult({ isWin: false, correctWord: targetWord });
      setShowStats(true);
      return;
    }

    // If we get here, the guess was wrong but there are still rows left
    // DON'T update stats here - game is still ongoing
    setActiveRow(activeRow + 1);
    setUsedLetters([]);
    setUsedLetterIndices([]);
  }, [activeRow, gameOver, guesses, targetWord, isValidRow, flipRow]);

  // Backspace function
  const handleBackspace = useCallback(() => {
    setGuesses((prevGuesses) => {
      const row = [...prevGuesses[activeRow]];
      const filledIndices = row
        .map((l, i) => (l !== "" ? i : -1))
        .filter((i) => i !== -1);

      if (filledIndices.length === 0) return prevGuesses;

      const lastFilledIdx = filledIndices[filledIndices.length - 1];
      const removedLetter = row[lastFilledIdx];
      row[lastFilledIdx] = "";
      const updated = prevGuesses.map((r, i) => (i === activeRow ? row : r));

      // Track which bank index corresponds to which grid position
      const letterCountBeforePosition = row
        .slice(0, lastFilledIdx)
        .filter((l) => l === removedLetter).length;

      // Find all bank indices for this letter that are used
      const bankIndicesForLetter = shuffledWord
        .split("")
        .map((l, idx) => (l === removedLetter ? idx : -1))
        .filter((idx) => idx !== -1 && usedLetterIndices.includes(idx))
        .sort((a, b) => a - b);

      // Remove the (letterCountBeforePosition + 1)th occurrence
      if (bankIndicesForLetter.length > letterCountBeforePosition) {
        const removeIdx = bankIndicesForLetter[letterCountBeforePosition];
        setUsedLetterIndices((prev) => prev.filter((i) => i !== removeIdx));
      }

      return updated;
    });
  }, [activeRow, shuffledWord, usedLetterIndices]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (gameOver) return;

      const key = e.key.toUpperCase();

      // Only letters
      if (key.length === 1 && key >= "A" && key <= "Z") {
        setGuesses((prevGuesses) => {
          const row = [...prevGuesses[activeRow]];
          const firstEmptyIdx = row.findIndex((l) => l === "");
          if (firstEmptyIdx === -1) return prevGuesses;

          row[firstEmptyIdx] = key;
          const updated = prevGuesses.map((r, i) =>
            i === activeRow ? row : r
          );

          const bankIdx = shuffledWord
            .split("")
            .findIndex(
              (l, idx) => l === key && !usedLetterIndices.includes(idx)
            );
          if (bankIdx !== -1) {
            setUsedLetterIndices((prev) => [...prev, bankIdx]);
          }

          return updated;
        });
      }

      // Backspace removes last filled letter
      if (key === "BACKSPACE") {
        handleBackspace();
      }

      // ✅ Enter/Return triggers submit
      if (key === "ENTER") {
        handleSubmit();
      }
    };

    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [
    activeRow,
    gameOver,
    shuffledWord,
    usedLetterIndices,
    handleSubmit,
    handleBackspace,
  ]);

  return (
    <div className="min-h-screen flex flex-col items-center bg-gray-900">
      <Header
        onStatsClick={() => setShowStats(true)}
        onHowToPlayClick={() => setShowHowToPlay(true)}
      />

      {showHowToPlay && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-xl p-6 max-w-md w-full mx-4 border border-gray-700">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-white">How to Play</h2>
              <button
                onClick={() => setShowHowToPlay(false)}
                className="text-gray-300 hover:text-white text-2xl"
              >
                ×
              </button>
            </div>
            <div className="text-gray-200 space-y-3">
              <p>• Rearrange the scrambled letters to form the correct word</p>
              <p>• Use the letter bank or your keyboard to input letters</p>
              <p>• Press Enter or click the arrow to submit your guess</p>
              <p>• Green tiles (G) show correct letters in correct positions</p>
              <p>• Red tiles (R) show correct letters in wrong positions</p>
              <p>• You have 5 attempts to guess the word</p>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col items-center justify-center p-4 pt-6">
      
      <div className="text-white text-sm mt-2">
        {remainingPermutations.toLocaleString()} Letter Combinations
      </div>

        <Grid
          guesses={guesses}
          resultColors={resultColors}
          activeRow={activeRow}
          wordLength={wordLength}
          flippingTiles={flippingTiles}
          onInputChange={() => {}}
        />

        <div className="flex items-start mb-4 mt-6 gap-4">
          {/* Letter bank container */}
          <div className="flex flex-col gap-2">
            {/* Top row - First 5 letters */}
            <div className="flex justify-center gap-2">
              {[0, 1, 2, 3, 4].map((position) => {
                const letterIndex = letterPositions.indexOf(position);
                const letter = shuffledWord[letterIndex];
                const isUsed = usedLetterIndices.includes(letterIndex);

                return (
                  <div
                    key={position}
                    onClick={() => {
                      if (gameOver || isUsed) return;
                      const firstEmptyIdx = guesses[activeRow].findIndex(
                        (l) => l === ""
                      );
                      if (firstEmptyIdx === -1) return;

                      const updated = guesses.map((r, i) =>
                        i === activeRow
                          ? r.map((l, j) => (j === firstEmptyIdx ? letter : l))
                          : r
                      );
                      setGuesses(updated);
                      setUsedLetterIndices((prev) => [...prev, letterIndex]);
                    }}
                    className={`w-9 h-9 flex items-center justify-center rounded-md border transition-all duration-300 ease-in-out
          ${
            isUsed
              ? "bg-gray-400 text-gray-200 border-gray-300"
              : "bg-white text-black border-gray-400 cursor-pointer"
          } select-none`}
                  >
                    {letter}
                  </div>
                );
              })}
            </div>

            {/* Bottom row - Last 4 letters */}
            <div className="flex justify-center gap-2">
              {[5, 6, 7, 8].map((position) => {
                const letterIndex = letterPositions.indexOf(position);
                const letter = shuffledWord[letterIndex];
                const isUsed = usedLetterIndices.includes(letterIndex);

                return (
                  <div
                    key={position}
                    onClick={() => {
                      if (gameOver || isUsed) return;
                      const firstEmptyIdx = guesses[activeRow].findIndex(
                        (l) => l === ""
                      );
                      if (firstEmptyIdx === -1) return;

                      const updated = guesses.map((r, i) =>
                        i === activeRow
                          ? r.map((l, j) => (j === firstEmptyIdx ? letter : l))
                          : r
                      );
                      setGuesses(updated);
                      setUsedLetterIndices((prev) => [...prev, letterIndex]);
                    }}
                    className={`w-9 h-9 flex items-center justify-center rounded-md border transition-all duration-300 ease-in-out
          ${
            isUsed
              ? "bg-gray-400 text-gray-200 border-gray-300"
              : "bg-white text-black border-gray-400 cursor-pointer"
          } select-none`}
                  >
                    {letter}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Buttons container - Backspace and Enter stacked vertically */}
          <div className="flex flex-col gap-2">
            {/* Backspace button - top right */}
            <button
              onClick={handleBackspace}
              disabled={
                gameOver || guesses[activeRow].every((letter) => letter === "")
              }
              onMouseDown={(e) => e.preventDefault()}
              className={`w-9 h-9 rounded-lg shadow transition flex items-center justify-center
                ${
                  gameOver ||
                  guesses[activeRow].every((letter) => letter === "")
                    ? "bg-gray-400 text-gray-200 cursor-not-allowed"
                    : "bg-red-600 text-white hover:bg-red-700"
                }`}
              title="Backspace"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2M3 12l6.414 6.414a2 2 0 001.414.586H19a2 2 0 002-2V7a2 2 0 00-2-2h-8.172a2 2 0 00-1.414.586L3 12z"
                />
              </svg>
            </button>

            <button
              onClick={handleShuffle}
              disabled={gameOver}
              onMouseDown={(e) => e.preventDefault()}
              className={`w-9 h-9 rounded-lg shadow transition flex items-center justify-center
    ${
      gameOver
        ? "bg-gray-400 text-gray-200 cursor-not-allowed"
        : "bg-purple-600 text-white hover:bg-purple-700"
    }`}
              title="Shuffle"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Buttons Container - Wrap both buttons */}
        <div className="flex gap-2 mt-4">
          <button
            onClick={handleSubmit}
            disabled={!isValidRow() || gameOver}
            className={`w-16 h-9 rounded-lg shadow transition flex items-center justify-center
    ${
      !isValidRow() || gameOver
        ? "bg-gray-400 cursor-not-allowed"
        : "bg-green-600 hover:bg-green-700"
    }`}
            title="Submit"
          >
            <span
              className={`text-xs font-medium ${
                !isValidRow() || gameOver ? "text-gray-200" : "text-white"
              }`}
            >
              Submit
            </span>
          </button>
          {/* Added flex container with gap */}
          {/* <button
            onClick={(e) => {
              generateWord();
              e.currentTarget.blur();
            }}
            onMouseDown={(e) => e.preventDefault()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg shadow hover:bg-blue-700 transition focus:outline-none select-none"
          >
            Play Again
          </button> */}
        </div>

        {/* Stats Modal - Replaces the old Modal */}
        {showStats && (
          <Stats
            gameResult={gameResult || undefined}
            onClose={() => {
              setShowStats(false);
              if (gameResult) {
                generateWord(); // Start new game if closing after game over
              }
            }}
          />
        )}
      </div>
    </div>
  );
};

export default App;
